import { useCallback, useEffect, useMemo, useRef, useState } from "react";

/** Данные из журнала receiptTransaction; превью чека — только через ECC-прокси (`downloadImage`), без fileId. */
import ReceiptTransaction from "../services/ReceiptTransaction";
import { ReceiptStatus } from "../services/ReceiptProvider/types";
import {
	isReceiptStatusInProgress,
	isReceiptStatusTerminal,
} from "../utils/receiptDisplay.util";

export interface UseOrderReceiptTransactionOptions {
	successOnly?: boolean;
	limit?: number;
	pollIntervalMs?: number;
}

const DEFAULT_POLL_INTERVAL_MS = 2000;
const DEFAULT_POLL_MAX_ATTEMPTS = 30;

function pickLatestTransaction(
	items: ReceiptTransaction.Model[],
	successOnly: boolean,
): ReceiptTransaction.Model | null {
	if (!items.length) return null;

	if (successOnly) {
		return (
			items.find((item) => item.status === ReceiptStatus.SUCCESS) ??
			items[0] ??
			null
		);
	}

	return items[0] ?? null;
}

function mergeTransactionItems(
	prev: ReceiptTransaction.Model[],
	incoming: ReceiptTransaction.Model | ReceiptTransaction.Model[],
	limit: number,
): ReceiptTransaction.Model[] {
	const list = Array.isArray(incoming) ? incoming : [incoming];
	const merged = [...list];

	for (const item of prev) {
		if (!merged.some((entry) => entry.id === item.id)) {
			merged.push(item);
		}
	}

	merged.sort(
		(left, right) =>
			new Date(right.createdAt).getTime() -
			new Date(left.createdAt).getTime(),
	);

	return merged.slice(0, limit);
}

function sleep(ms: number): Promise<void> {
	return new Promise((resolve) => {
		window.setTimeout(resolve, ms);
	});
}

export default function useOrderReceiptTransaction(
	orderId?: number | null,
	options: UseOrderReceiptTransactionOptions = {},
) {
	const {
		successOnly = false,
		limit = successOnly ? 5 : 1,
		pollIntervalMs = DEFAULT_POLL_INTERVAL_MS,
	} = options;

	const [loading, setLoading] = useState(false);
	const [items, setItems] = useState<ReceiptTransaction.Model[]>([]);
	const pollTimerRef = useRef<number | null>(null);

	const transaction = useMemo(
		() => pickLatestTransaction(items, successOnly),
		[items, successOnly],
	);

	const successTransaction = useMemo(
		() =>
			items.find((item) => item.status === ReceiptStatus.SUCCESS) ??
			null,
		[items],
	);

	const canPreviewReceipt = useMemo(
		() =>
			successTransaction?.status === ReceiptStatus.SUCCESS &&
			!!successTransaction?.id &&
			!!successTransaction?.externalId,
		[successTransaction],
	);

	const fetchItems = useCallback(async () => {
		if (!orderId) return [];

		return ReceiptTransaction.getAll({
			orderIds: [orderId],
			...(successOnly ? { statuses: [ReceiptStatus.SUCCESS] } : {}),
			limit,
		});
	}, [orderId, successOnly, limit]);

	const reload = useCallback(async () => {
		if (!orderId) {
			setItems([]);
			return;
		}

		setLoading(true);
		try {
			const nextItems = await fetchItems();
			setItems(nextItems);
		} finally {
			setLoading(false);
		}
	}, [fetchItems, orderId]);

	const applyTransaction = useCallback(
		(tx: ReceiptTransaction.Model) => {
			setItems((prev) => mergeTransactionItems(prev, tx, limit));
		},
		[limit],
	);

	const pollUntilSettled = useCallback(async () => {
		if (!orderId) return;

		for (let attempt = 0; attempt < DEFAULT_POLL_MAX_ATTEMPTS; attempt += 1) {
			await sleep(pollIntervalMs);
			const nextItems = await fetchItems();
			setItems(nextItems);

			const latest = pickLatestTransaction(nextItems, successOnly);
			if (latest && isReceiptStatusTerminal(latest.status)) {
				return;
			}
		}
	}, [fetchItems, orderId, pollIntervalMs, successOnly]);

	useEffect(() => {
		let cancelled = false;

		if (!orderId) {
			setItems([]);
			return undefined;
		}

		setLoading(true);
		fetchItems()
			.then((nextItems) => {
				if (!cancelled) setItems(nextItems);
			})
			.finally(() => {
				if (!cancelled) setLoading(false);
			});

		return () => {
			cancelled = true;
		};
	}, [fetchItems, orderId]);

	useEffect(() => {
		if (pollTimerRef.current != null) {
			window.clearInterval(pollTimerRef.current);
			pollTimerRef.current = null;
		}

		if (!orderId || !isReceiptStatusInProgress(transaction?.status)) {
			return undefined;
		}

		const poll = async () => {
			const nextItems = await fetchItems();
			setItems(nextItems);
		};

		pollTimerRef.current = window.setInterval(poll, pollIntervalMs);

		return () => {
			if (pollTimerRef.current != null) {
				window.clearInterval(pollTimerRef.current);
				pollTimerRef.current = null;
			}
		};
	}, [fetchItems, orderId, pollIntervalMs, transaction?.id, transaction?.status]);

	return {
		transaction,
		successTransaction,
		canPreviewReceipt,
		loading,
		reload,
		applyTransaction,
		pollUntilSettled,
	};
}
